import React, { useState } from "react";
import MindWiseService, { type MindWiseWeeklyStats } from "@/services/MindWiseService";
import {
  Brain,
  CheckCircle2,
  Calendar,
  Clock,
  RefreshCw,
  ArrowUpRight,
  ShieldCheck,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface MindWiseDashboardBannerProps {
  onOpenRosterModal: () => void;
}

export const MindWiseDashboardBanner: React.FC<MindWiseDashboardBannerProps> = ({
  onOpenRosterModal
}) => {
  const [stats, setStats] = useState<MindWiseWeeklyStats>(() => MindWiseService.getWeeklyIngestionStats());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await MindWiseService.pullPatientsFromMindWise();
      setStats(MindWiseService.getWeeklyIngestionStats());
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="relative rounded-2xl bg-gradient-to-r from-blue-50/90 via-indigo-50/50 to-emerald-50/60 border border-blue-200/80 p-5 shadow-xs overflow-hidden transition-all duration-200 hover:shadow-md">
      {/* Decorative background glow */}
      <div className="absolute top-0 right-0 -mt-6 -mr-6 w-48 h-48 bg-gradient-to-br from-blue-400/10 to-emerald-400/10 rounded-full blur-2xl pointer-events-none" />

      {/* ── Top row: Identity & Main Call to Action ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 relative z-10">
        <div className="flex items-start gap-3.5">
          <div className="p-2.5 rounded-xl bg-blue-600 text-white shadow-md shadow-blue-500/20 shrink-0 mt-0.5">
            <Brain className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-base font-bold text-gray-900 tracking-tight">
                MindWise EHR Weekly Ingestion
              </h2>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-2xs">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                Ingested This Week (Cycle 40)
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">
              5 patients synchronized from MindWise EMR roster for this weekly outreach cycle. Automated behavioral health questionnaires (PHQ-9 / GAD-7) active.
            </p>
          </div>
        </div>

        {/* ── Action buttons ── */}
        <div className="flex items-center gap-2 self-start md:self-auto shrink-0 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="text-xs gap-1.5 bg-white border-blue-200 text-blue-900 hover:bg-blue-50 hover:border-blue-300"
          >
            <RefreshCw className={`h-3.5 w-3.5 text-blue-600 ${isRefreshing ? "animate-spin" : ""}`} />
            {isRefreshing ? "Checking..." : "Sync Roster"}
          </Button>

          <Button
            size="sm"
            onClick={onOpenRosterModal}
            className="text-xs gap-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-xs"
          >
            <Users className="h-3.5 w-3.5" />
            <span>View Ingested Patients ({stats.ingestedCount})</span>
            <ArrowUpRight className="h-3.5 w-3.5 opacity-80" />
          </Button>
        </div>
      </div>

      {/* ── Bottom row: 4 Micro-Metric Tiles ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4 pt-4 border-t border-blue-200/60 relative z-10">
        {/* Tile 1: Ingested Patients */}
        <div 
          onClick={onOpenRosterModal}
          className="bg-white/80 backdrop-blur-xs rounded-xl p-3 border border-blue-100 hover:border-blue-300 transition-colors cursor-pointer group"
        >
          <div className="text-[11px] font-medium text-gray-500 flex items-center justify-between">
            <span>Ingested Patients</span>
            <ArrowUpRight className="h-3 w-3 text-gray-400 group-hover:text-blue-600 transition-colors" />
          </div>
          <div className="text-xl font-bold text-gray-900 mt-0.5">
            {stats.ingestedCount} <span className="text-xs font-normal text-gray-500">Active</span>
          </div>
          <div className="text-[10px] text-blue-700 mt-1 font-medium">
            All 5 mapped with EHR UUIDs
          </div>
        </div>

        {/* Tile 2: Assessments Filed */}
        <div 
          onClick={onOpenRosterModal}
          className="bg-white/80 backdrop-blur-xs rounded-xl p-3 border border-blue-100 hover:border-blue-300 transition-colors cursor-pointer group"
        >
          <div className="text-[11px] font-medium text-gray-500 flex items-center justify-between">
            <span>Screenings Synced</span>
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
          </div>
          <div className="text-xl font-bold text-emerald-700 mt-0.5">
            {stats.completedScreenings} <span className="text-xs font-normal text-gray-500">of {stats.ingestedCount}</span>
          </div>
          <div className="text-[10px] text-gray-500 mt-1">
            PHQ-9 & GAD-7 filed to EHR
          </div>
        </div>

        {/* Tile 3: Schedule & Next Run */}
        <div className="bg-white/80 backdrop-blur-xs rounded-xl p-3 border border-blue-100">
          <div className="text-[11px] font-medium text-gray-500 flex items-center justify-between">
            <span>Ingestion Cadence</span>
            <Calendar className="h-3 w-3 text-blue-500" />
          </div>
          <div className="text-sm font-bold text-gray-900 mt-1">
            Weekly (Mondays)
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
            <Clock className="h-2.5 w-2.5 text-gray-400" />
            Next: Oct 05, 08:00 AM
          </div>
        </div>

        {/* Tile 4: Health & Endpoint */}
        <div className="bg-white/80 backdrop-blur-xs rounded-xl p-3 border border-blue-100">
          <div className="text-[11px] font-medium text-gray-500 flex items-center justify-between">
            <span>Pipeline Connector</span>
            <ShieldCheck className="h-3 w-3 text-emerald-600" />
          </div>
          <div className="text-sm font-bold text-gray-900 mt-1 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            Connected (REST v2)
          </div>
          <div className="text-[10px] text-emerald-700 mt-0.5 font-medium">
            100% Sync Health · Ready
          </div>
        </div>
      </div>
    </div>
  );
};

export default MindWiseDashboardBanner;
